import React, { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import toast from 'react-hot-toast';
import { TStoreInventory } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import { DataLayerOnLoad } from 'helpers/dataLayerHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import aggregatedQuantity from 'helpers/utils/aggregatedQuantity';
import { sdk } from 'sdk';
import { Event } from '@commercetools/frontend-sdk';
import { useAccount, useCart, useStock } from 'frontastic/hooks';
import Header, { Props as HeaderProps } from './components/header';
import Secure from './components/secure';
import Steps from './components/steps';
import PaymentHelper from './components/steps/sections/payment/helper';
import { RedirectContainer } from './components/steps/sections/payment/redirect';
import CheckoutProvider, { useAdyenCheckout, useCheckout } from './provider';
import { DeliveryOptionsProps } from './types';
import OrderSummary from '../order-summary';

export type CheckoutWrappedProps = HeaderProps & DeliveryOptionsProps;

const CheckoutWrapped: React.FC<CheckoutWrappedProps> = ({
  logo,
  homeDeliveryOptionUk,
  homeDeliveryDescriptionUk,
  homeDeliveryOptionEu,
  homeDeliveryDescriptionEu,
  storeCollectionOption,
  storeCollectionDescription,
  ...emptyState
}) => {
  const searchParams = useSearchParams();
  const ctSession = searchParams.get('ctSession');
  const [active, setActive] = useState<number>(0);

  const { setHasOutOfStockItems, scrollToError, setPreviousPaymentMethod } = useCheckout();

  useLocale();
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const paymentHelper = PaymentHelper();
  const { getCountry, getCurrency } = useLocale();
  const deliveryOptions = [
    {
      deliveryOptionId: getCurrency() === 'GBP' ? homeDeliveryOptionUk : homeDeliveryOptionEu,
      deliveryOptionDesc: getCurrency() === 'GBP' ? homeDeliveryDescriptionUk : homeDeliveryDescriptionEu,
    },
    {
      deliveryOptionId: storeCollectionOption,
      deliveryOptionDesc: storeCollectionDescription,
    },
  ];

  const [isFinalStep, setIsFinalStep] = useState(false);
  const { triggerGetStock } = useStock();
  const { data: cartData, productSKUs, removeInvalidDiscountCodes } = useCart();

  triggerGetStock(productSKUs as string[]);

  const { processing, setProcessing, selectedPaymentMethod, paymentStatus } = useAdyenCheckout();
  const { account } = useAccount();
  const eventListeners = useMemo(() => new Set<() => void>(), []);

  const checkStock = useCallback(async (productSKUs: string[], countryCode: string) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.stock.getProductInventory({ productSKUs, countryCode });
    if (!res.isError) {
      return res.data as TStoreInventory[];
    }
    return [];
  }, []);

  const hasUnavailableItems = (fluentStocks: TStoreInventory[]) => {
    return cartData?.lineItems?.some((item) => {
      if (item?.isPerpetualStock) {
        return;
      }
      const availableQuantity = aggregatedQuantity(item.variant?.sku, fluentStocks);
      const hasInsufficientStocks = item.count && item.count > availableQuantity;
      const hasOutOfStockItems = availableQuantity === 0;
      return hasOutOfStockItems || hasInsufficientStocks;
    });
  };

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    localStorage.setItem('retainComponentData', 'true');
    event.preventDefault();
    return null;
  };

  useEffect(() => {
    const addEventListener = (type: string, listener: EventListenerOrEventListenerObject) => {
      window.addEventListener(type, listener);
      eventListeners.add(() => window.removeEventListener(type, listener));
    };

    addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      eventListeners.forEach((removeListener) => removeListener());
    };
  }, [eventListeners]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (cartData?.discountCodes?.length) {
        removeInvalidDiscountCodes(cartData.discountCodes);
      }
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [cartData?.discountCodes]);

  const checkStockAndProceed = async () => {
    eventListeners.forEach((removeListener) => removeListener());
    try {
      const fluentStock = await checkStock(productSKUs as string[], getCountry());
      const hasNoStockItems = hasUnavailableItems(fluentStock);
      if (hasNoStockItems) {
        setActive(active - 1);
        setHasOutOfStockItems(hasNoStockItems);
        scrollToError('paymentSection');
      } else {
        setProcessing(true);
        setPreviousPaymentMethod(selectedPaymentMethod);
        paymentHelper.submitPayment();
      }
    } catch (e) {
      toast.error('Unfortunately, something went wrong while processing your payment.');
      setProcessing(false);
    }
  };

  const onLogoClicked = () => {
    eventListeners.forEach((removeListener) => removeListener());
    window.location.href = '/';
  };

  const removeGiftCardEventHandler = (event: Event<'removeGiftcardClicked', { paymentStepIndex: number }>) => {
    // Perform custom event handling logic here.
    if (isFinalStep) {
      setIsFinalStep(false);
      setActive(event.data.paymentStepIndex);
    }
  };

  useEffect(() => {
    sdk.on('removeGiftcardClicked', removeGiftCardEventHandler);
    return () => {
      sdk.off('removeGiftcardClicked', removeGiftCardEventHandler);
    };
  }, [isFinalStep]);

  useEffect(() => {
    const localeData = {
      country: getCountry(),
      currency: getCurrency(),
    };

    const currentUrl = document.location.origin;
    const dataLayer = new DataLayerOnLoad(currentUrl, 'checkout', localeData, account);
    dataLayer.setDataLayerOnLoad();
  }, []);

  if (ctSession) {
    return (
      <>
        <div className="min-h-screen lg:bg-neutral-200">
          <Header logo={logo} {...emptyState} isCartRequired={false} />

          <div className="h-full">
            <RedirectContainer></RedirectContainer>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen pb-48 lg:bg-neutral-200">
      {/* As of now commentted for UI perspective */}
      {/* <div onClick={onLogoClicked}>
        <Header logo={logo} {...emptyState} isCheckoutPage={true} />
      </div> */}
      <div className="lg:mx-[48px]">
        <Secure />
        <div className="flex-row-reverse items-start gap-24 lg:flex">
          <OrderSummary
            className="bg-white px-16 md:px-24 lg:w-[30%] lg:rounded-md lg:p-36 lg:px-20 xl:px-48"
            includeSummaryAccordion
            title={formatCartMessage({ id: 'order.summary', defaultMessage: 'Order summary' })}
            classNames={{
              applyDiscountButton: 'lg:mb-0 py-14 text-16 mb-16 border-b border-neutral-400 lg:border-b-transparent',
              totalAmount: 'text-18 md:pb-20',
              subCostsContainer: 'pt-12 md:pt-16 mb-20 lg:py-24 lg:mb-16 lg:border-b border-neutral-400',
            }}
            button={
              selectedPaymentMethod !== 'paypal' && paymentStatus !== '3ds2' ? (
                <Button
                  variant="primary"
                  disabled={!isFinalStep}
                  className="w-full uppercase"
                  type="submit"
                  onClick={checkStockAndProceed}
                  loading={processing}
                  size="m"
                >
                  {formatCheckoutMessage({ id: 'complete.purchase', defaultMessage: 'Complete purchase' })}
                </Button>
              ) : (
                <></>
              )
            }
            isFreeDeliveryBannerRequired={false}
          />
          <Steps
            onFinalStepChange={setIsFinalStep}
            deliveryOptions={deliveryOptions}
            active={active}
            setActive={setActive}
            onSubmitPayment={checkStockAndProceed}
          />
        </div>
      </div>
    </div>
  );
};

const Checkout: FC<CheckoutWrappedProps> = (props) => (
  <CheckoutProvider>
    <CheckoutWrapped {...props} />
  </CheckoutProvider>
);

export default Checkout;
